import { action, computed, observable, makeAutoObservable } from 'mobx';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { cloneDeep } from 'lodash';
import { TopEventsSportState } from 'src/main/cms/TopEvents/topEventsState/TopEventsSportState';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getAllTopEventsSports';

export type TopEventsSportDataModelType = Response200Type[number];

export type CompetitionCombinedType = TopEventsSportDataModelType['competitions_list'][number];

export type EventCombinedType = CompetitionCombinedType['events_list'][number];

export class TopEventsEventsState {
    private readonly topEventsEventSportStateMap: MobxMapAutoNew<string, TopEventsSportState>;

    @observable public readonly topEventsEventsResource: Resource<Array<TopEventsSportDataModelType>>;
    @observable.ref public sportsArray: Array<TopEventsSportDataModelType> = [];
    @observable public isLoading: boolean = false;

    public constructor(private readonly trpcClient: TrpcClient) {
        this.topEventsEventsResource = new Resource(async (): Promise<Array<TopEventsSportDataModelType>> => {
            const response = await this.trpcClient.client.topEventsSports.getAllTopEventsSports.query();
            if (response.status === 200) {
                this.sportsArray = response.body;
                return response.body;
            }

            return [];
        });

        this.topEventsEventSportStateMap = new MobxMapAutoNew(
            (sportSlug: string): TopEventsSportState =>
                new TopEventsSportState(trpcClient, sportSlug, this.getSportData, this)
        );

        makeAutoObservable(this);
    }

    public getTopEventsEventSportStateBySportSlug = (sportSlug: string): TopEventsSportState => {
        return this.topEventsEventSportStateMap.get(sportSlug);
    };

    @computed public get isTopEventsEventsDataLoading(): boolean {
        const topEventsSportsData = this.topEventsEventsResource.get();
        return topEventsSportsData.type === 'loading';
    }

    @action public getSportData = (sportSlug: string): TopEventsSportDataModelType | null => {
        return this.sportsArray.find((sport) => sport.sport_id === sportSlug) ?? null;
    };

    @action public setSportsDataFromResource = (): void => {
        this.topEventsEventsResource.get();
        this.sportsArray = [];
        if (this.topEventsEventsData === null) {
            return;
        }
        this.sportsArray = this.topEventsEventsData;
    };

    @action public setSportsData = (data: Array<TopEventsSportDataModelType>): void => {
        const tempArray = cloneDeep(data);
        this.sportsArray = tempArray;
    };

    @action public setIsLoading = (val: boolean): void => {
        this.isLoading = val;
    };

    @computed public get topEventsEventsData(): Array<TopEventsSportDataModelType> | null {
        const topEventsEventsData = this.topEventsEventsResource.get();
        if (topEventsEventsData.type === 'ready') {
            return topEventsEventsData.value;
        }
        return null;
    }

    public getCompetitionsArrayForDraggableList = (sportId: string): Array<CompetitionCombinedType> => {
        return this.sportsArray.find((sportsElem) => sportsElem.sport_id === sportId)?.competitions_list ?? [];
    };

    public setCompetitionsArrayForDraggableList = (
        sportId: string,
        competitionsList: Array<CompetitionCombinedType>
    ): Array<TopEventsSportDataModelType> => {
        const tempArr = [];
        for (const value of this.sportsArray) {
            if (value.sport_id === sportId) {
                tempArr.push({
                    competitions_list: competitionsList,
                    display_order: value.display_order,
                    universe: value.universe,
                    sport_id: value.sport_id,
                    id: value.id,
                    is_active: value.is_active,
                });
            } else {
                tempArr.push(value);
            }
        }
        return tempArr;
    };

    public setEventsArrayForDraggableList = (
        sportId: string,
        competitionId: number,
        eventsList: Array<EventCombinedType>
    ): Array<TopEventsSportDataModelType> => {
        const tempArr = [];
        for (const value of this.sportsArray) {
            if (value.sport_id === sportId) {
                tempArr.push({
                    competitions_list: value.competitions_list.map((e) => {
                        if (e.competition_id === competitionId) {
                            return {
                                ...e,
                                events_list: eventsList,
                            };
                        } else {
                            return {
                                ...e,
                            };
                        }
                    }),
                    display_order: value.display_order,
                    universe: value.universe,
                    sport_id: value.sport_id,
                    id: value.id,
                    is_active: value.is_active,
                });
            } else {
                tempArr.push(value);
            }
        }
        return tempArr;
    };

    public getEventsArrayForDraggableList = (
        sportId: string,
        competitionId: number
    ): Array<EventCombinedType> | undefined => {
        const competitionsData = this.getCompetitionsArrayForDraggableList(sportId);
        return competitionsData.find((competition) => competition.competition_id === competitionId)?.events_list;
    };
}
